import React, { useContext, useState, useEffect, useRef }  from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  // faEllipsisV,
  faExternalLinkAlt, faClipboardList,
  faCloudShowersHeavy, faCalendarCheck
  } from "@fortawesome/free-solid-svg-icons";
import { AppDivisionContext } from "../../../../contexts/AppDivisionContext";
import { customDashboard } from "../../../../utils/api/dashboard";
import { createCancelSource, isRequestCancelled } from "../../../../utils/xhrService";
import DashboardContext from "../../../../contexts/DashboardContext";
import MonthViewLayout from './MonthViewLayout'
import EllipsisPopover from '../../shared/EllipsisPopover/EllipsisPopover'
import CalendarFilterPanel from './CalendarFilterPanel'




/**
 * SW2-2325 — how long a month already fetched stays good.
 *
 * A 304 is not cheap on this endpoint: the server runs the whole query, builds the
 * response — up to 1.5MB for a busy month across a division — hashes it, compares the
 * ETag and throws it away. Measured over 30 requests while paging back and forth: 16
 * were 304s averaging 207ms, **3.3 seconds of server time that returned no bytes**,
 * half the total spent. Conditional GETs save the download, not the work.
 *
 * Past months are settled history, so they are held for the session. The current month
 * can gain an inspection while the dashboard is open, so it gets a short life instead.
 */
const CURRENT_MONTH_TTL_MS = 60 * 1000
const MAX_CACHED_RESPONSES = 24

const monthKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`

const Calendar = (props) => {
  const { getPath, appDivisionId } = useContext(AppDivisionContext);
  const {
    setDisplayStatsForCal, setIsLoadingForCal, projectsListForCal,
    setPrecipitationDataForCal,
    setUpcomingDataForCal,
    selectedCalProjects, setSelectedCalProjects,
    selectedCalInspectionTypes, setSelectedCalInspectionTypes,
    calFilterGroups, setCalFilterGroups,
  } = useContext(DashboardContext)
  const widgetRef = useRef(0);
  const [ widgetWidth, setWidgetWidth ] = useState(widgetRef.current.clientWidth)
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const [ currentCalMonth, setCurrentCalMonth ] = useState("")

  // Styles changes based on widget width
  let showMoreResponsiveButton = {
    fontSize: "0.9rem",
    paddingRight: "0.5rem 0rem 0.5rem 0.5rem"
  }

  useEffect(() => {
    function handleResize() {
      setWidgetWidth(widgetRef.current.clientWidth)
    }

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  })

  // SW2-2325 — the grouping options are counts only, so this is one small request
  // rather than a walk over every project in the division.
  useEffect(() => {
    // A flag is enough here: this fires once per division, and the response is counts
    // only — 3KB, measured at 89ms. Nothing worth threading a cancel token through
    // `customDashboard.fetch` for.
    let cancelled = false

    customDashboard.fetch(getPath('/calendar/filterGroups'))
      .then((groups) => { if (!cancelled) { setCalFilterGroups(groups); setFilterGroupsFailed(false) } })
      // A rejection used to be logged and nothing else, so `calFilterGroups` stayed
      // null and the panel showed "Loading projects…" for ever. The backend answers
      // these with a real status now, which makes a perpetual spinner a lie rather
      // than merely unhelpful.
      .catch((error) => {
        console.log(error)
        if (!cancelled) setFilterGroupsFailed(true)
      })

    return () => { cancelled = true }
    // `getPath` is rebuilt on every render of AppDivisionProvider, so depending on it
    // would refetch continuously; `appDivisionId` is what actually changes the path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appDivisionId])

  const [filterGroupsFailed, setFilterGroupsFailed] = useState(false)

  // Keyed by month + selection, so switching filters never serves the wrong month's
  // data. A ref, not state: writing to it must not trigger a render.
  const responseCache = useRef(new Map())
  // Precipitation is cached separately because it depends on fewer things — see the
  // effect below.
  const precipitationCache = useRef(new Map())
  // SW2-2343 — upcoming, likewise: it does not depend on the inspection-type filter.
  const upcomingCache = useRef(new Map())

  // Project ids do not carry across divisions, so neither does anything keyed by them.
  useEffect(() => {
    responseCache.current.clear()
    precipitationCache.current.clear()
    upcomingCache.current.clear()
    setFilterGroupsFailed(false)
  }, [appDivisionId])

  /**
   * SW2-2325 — how the project selection travels in the query string.
   *
   * Comma-joined: axios 0.18 serialises an array as `projectIds[]=1234&projectIds[]=…`,
   * about 18 bytes an id, against 5 for `projectIds=1234,…`. For the largest selection
   * that matters — an admin with all 1,306 projects in CMS - Colorado once SW2-2342
   * lands — that is 26KB against **6.6KB**, and nginx's default header buffer is 8,192.
   *
   * **No complement.** An earlier version sent whichever of the selection or its
   * inverse was shorter. It was withdrawn: the complement is computed from the
   * browser's project list, and equal lengths do not prove the two sides hold the same
   * ids. If they drift by one — a project archived between `/fetchAllUserProjects` and
   * the calendar query — that project is absent from the exclusion list and therefore
   * *included*, so the user sees a project they never selected. Unlikely, but wrong in
   * a way nobody would notice, and comma-joining alone already fits.
   *
   * The ceiling is now ~1,500 projects in a single selection (9,667 bytes at 1,600
   * with five-digit ids). SW2-2342 is where selections get that large, and it is where
   * a server-authoritative form belongs — group references, or a complement carrying a
   * version of the id set the server can verify. The backend still accepts
   * `excludeProjectIds` for that.
   */
  const selectionParams = (selected) =>
    selected.length ? { projectIds: selected.join(',') } : {}
  // eslint-disable-next-line react-hooks/exhaustive-deps

  /**
   * One fetch, driven by the two things the query depends on: the month and
   * the selection. It also covers the first paint, which is the actual behaviour change:
   * the widget used to render an empty grid until a project was chosen, because nothing
   * asked for data. Show-all is the default now, so the division's month loads on mount.
   */
  useEffect(() => {
    if (!currentCalMonth) return

    const cacheKey = JSON.stringify([
      currentCalMonth,
      [...selectedCalProjects].sort((a, b) => a - b),
      [...selectedCalInspectionTypes].sort((a, b) => a - b),
    ])
    const cached = responseCache.current.get(cacheKey)
    const isSettled = currentCalMonth < monthKey(new Date())

    if (cached && (isSettled || Date.now() - cached.at < CURRENT_MONTH_TTL_MS)) {
      setDisplayStatsForCal(cached.inspections)
      setIsLoadingForCal(false)

      return undefined
    }

    /**
     * Superseded requests are abandoned, not merely ignored.
     *
     * A flag only discards the response once it has already arrived. Measured while
     * paging twelve months back with a whole division selected: twelve requests, every
     * one downloaded in full — **16.4 MB** to end up looking at one month, each response
     * about 1 MB.
     *
     * What this saves is the transfer and everything after it: the download, the JSON
     * parse, and the state update plus re-render for data nobody will see. It does *not*
     * save the server work — Express carries on after the client disconnects, so the
     * query still runs. Cancelling that too would mean wiring `req.on("close")` through
     * to a `pg_cancel_backend`, which is a different piece of work.
     *
     * The mechanism lives in `xhrService` rather than here: axios is pinned at 0.18.1,
     * which predates `signal` support, and keeping `CancelToken` behind the client
     * boundary means a version bump changes one file rather than every caller.
     */
    const source = createCancelSource()

    setIsLoadingForCal(true)


    customDashboard.fetchWithParams(getPath('/calendar/getCalendarData'), {
      ...selectionParams(selectedCalProjects),
      // Empty means every type; the list is short enough that it never needs
      // inverting the way the project selection does.
      ...(selectedCalInspectionTypes.length
        ? { inspectionTypeIds: selectedCalInspectionTypes.join(',') }
        : {}),
      month: currentCalMonth,
    }, { cancelToken: source.token })
    .then((calendarResponse) => {
      // Oldest out first. A Map keeps insertion order, and a plain FIFO is enough here —
      // the bound exists to stop a long session holding every month of every selection,
      // not to be clever about which to keep.
      if (responseCache.current.size >= MAX_CACHED_RESPONSES) {
        responseCache.current.delete(responseCache.current.keys().next().value)
      }

      responseCache.current.set(cacheKey, {
        inspections: calendarResponse.inspections,
        at: Date.now(),
      })

      setDisplayStatsForCal(calendarResponse.inspections)
      setIsLoadingForCal(false)
    })
    .catch((error) => {
      // A cancellation is this effect superseding itself; the request that replaced it
      // owns the spinner now, so leave it alone rather than flashing it off and on.
      if (isRequestCancelled(error)) return

      console.log(error)
      setIsLoadingForCal(false)
    })

    // A month paged through quickly would otherwise land out of order — the
    // division-wide query is the slow one, and the one a fast pager fires repeatedly.
    return () => source.cancel('superseded')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCalMonth, selectedCalProjects, selectedCalInspectionTypes, appDivisionId])

  /**
   * SW2-2338 — precipitation, on its own effect.
   *
   * It shared the inspections request until review pointed out the consequence:
   * precipitation does not depend on the **inspection-type** filter, but the shared
   * effect and shared cache key both did — so every type toggle refetched the whole
   * precipitation query and burned another cache entry, for a result that could not
   * have changed.
   *
   * Keyed on month, project selection and division only.
   */
  useEffect(() => {
    if (!currentCalMonth) return undefined

    const cacheKey = JSON.stringify([
      currentCalMonth,
      [...selectedCalProjects].sort((a, b) => a - b),
    ])
    const cached = precipitationCache.current.get(cacheKey)
    const isSettled = currentCalMonth < monthKey(new Date())

    if (cached && (isSettled || Date.now() - cached.at < CURRENT_MONTH_TTL_MS)) {
      setPrecipitationDataForCal(cached.precipitation)

      return undefined
    }

    const source = createCancelSource()

    customDashboard.fetchWithParams(
      getPath('/calendar/precipitation'),
      { ...selectionParams(selectedCalProjects), month: currentCalMonth },
      { cancelToken: source.token },
    )
      .then((precipitation) => {
        if (precipitationCache.current.size >= MAX_CACHED_RESPONSES) {
          precipitationCache.current.delete(precipitationCache.current.keys().next().value)
        }

        precipitationCache.current.set(cacheKey, { precipitation, at: Date.now() })
        setPrecipitationDataForCal(precipitation)
      })
      .catch((error) => {
        if (isRequestCancelled(error)) return

        console.log(error)
        // An empty shape, not a stale one: the cells read `days[...]`, and leaving the
        // previous month's marks up would be worse than showing none.
        setPrecipitationDataForCal({ days: {} })
      })

    return () => source.cancel('superseded')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCalMonth, selectedCalProjects, appDivisionId])

  /**
   * SW2-2343 — upcoming inspections, on their own effect for the same reason as
   * precipitation.
   *
   * Keyed on month, project selection and division only — the same three things the
   * query itself takes.
   *
   * The inspection-type filter is absent from the key because it does not apply to
   * upcoming rows at all: that control lists `inspection_types`, which an inspection
   * only acquires once it has been created. See the note in `MonthViewLayout`'s cell,
   * where filtering on it was tried and turned out to compare two different tables'
   * ids that happen to share a range.
   */
  useEffect(() => {
    if (!currentCalMonth) return undefined

    const cacheKey = JSON.stringify([
      currentCalMonth,
      [...selectedCalProjects].sort((a, b) => a - b),
    ])
    const cached = upcomingCache.current.get(cacheKey)

    /**
     * No `isSettled` here, unlike the two effects above — the TTL applies to every
     * month, past ones included.
     *
     * Those two treat a past month as final and cache it with no expiry, which is
     * right for them: August's weather does not change, and neither does what was
     * submitted in August. A past month's *upcoming* rows do change, and by the
     * mechanism the past-due marker exists to show — completing overdue work
     * advances `next_inspection_date`, and the row leaves the month it was late in.
     *
     * Cached indefinitely, an inspector who pages back to August, sees red, clears
     * the work and returns would still see red until they switched division or
     * reloaded. Past months are where the red markers live, so that is the view
     * this feature is mostly looked at through.
     */
    if (cached && Date.now() - cached.at < CURRENT_MONTH_TTL_MS) {
      setUpcomingDataForCal(cached.upcoming)

      return undefined
    }

    const source = createCancelSource()

    customDashboard.fetchWithParams(
      getPath('/calendar/upcoming'),
      { ...selectionParams(selectedCalProjects), month: currentCalMonth },
      { cancelToken: source.token },
    )
      .then((upcoming) => {
        if (upcomingCache.current.size >= MAX_CACHED_RESPONSES) {
          upcomingCache.current.delete(upcomingCache.current.keys().next().value)
        }

        upcomingCache.current.set(cacheKey, { upcoming, at: Date.now() })
        setUpcomingDataForCal(upcoming)
      })
      .catch((error) => {
        if (isRequestCancelled(error)) return

        // Logged, like the two effects above. A swallowed failure here shows an empty
        // month, which is indistinguishable from a month with nothing due — so the
        // console line is the only way to tell them apart. Disabled explicitly rather
        // than left to raise the file's warning count, where a real warning about new
        // code would be lost among the existing ones.
        // eslint-disable-next-line no-console
        console.log(error)
        // An empty shape rather than a stale one, as precipitation does: the cells read
        // `days[...]`, and last month's marks would be worse than none.
        setUpcomingDataForCal({ days: {} })
      })

    return () => source.cancel('superseded')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCalMonth, selectedCalProjects, appDivisionId])

  // The selection is all this sets; the effect above owns the request. The old
  // `selectedCalProject` single-project state is gone with it — nothing read it once the
  // fetch stopped being split across two components.
  const handleSelectionChange = (projectIds) => setSelectedCalProjects(projectIds)


  const handleClose = () => {
    setAnchorEl(null);
  };





  let oneColumnHeader = {
    // gridTemplateAreas: '"title title title title . bars bars bars bars bars bars ellipsis"'
  }

  
  return(
    <>
      <div className="calendar-widget" ref={widgetRef}>
        <header className="calendar-widget__header"  style={widgetWidth > 700 ? oneColumnHeader : null}>
          <h2 className="calendar-widget__title">Calendar</h2>
          {/* <div className="calendar-widget__ellipsis-button">
            <span onClick={(event) => setAnchorEl(event.currentTarget)}>
              <FontAwesomeIcon icon={ faEllipsisV }/>
            </span>
          </div> */}
          <EllipsisPopover handleCloseFunc={handleClose} id={id} open={open} anchorEl={anchorEl} />
        </header>

        {/* SW2-2325 — deliberately outside `__header`. That block styles every descendant
            `span` as a 37x37 bordered box and every descendant `select` as
            `flex-basis: 100%`, which between them swallowed the group names and squeezed
            the panel's search field to nothing. Its own row is cheaper than overriding
            rules the rest of the header depends on. */}
        <div className="calendar-widget__filter">
          <CalendarFilterPanel
            projects={projectsListForCal || []}
            filterGroups={calFilterGroups}
            selected={selectedCalProjects}
            onChange={handleSelectionChange}
            selectedInspectionTypes={selectedCalInspectionTypes}
            onInspectionTypesChange={setSelectedCalInspectionTypes}
            groupsFailed={filterGroupsFailed}
          />
        </div>
  
        <div
          data-rbd-drag-handle-context-id={props.providedProps}
          data-rbd-drag-handle-draggable-id="gibberish"
          style={{
            // When you set the data-rbd-drag-handle-context-id, RBD applies cursor: grab, so we need to revert that
            cursor: "auto"
          }}
        >
          <div className="calendar-widget__body">
            <div className="calendar-widget__content">
              <MonthViewLayout currentCalMonth={currentCalMonth} setCurrentCalMonth={setCurrentCalMonth} />
            </div>

            <footer className="calendar-widget__footer">
                <ul>
                  <section>
                    {/* The keys carry their own classes rather than relying on their
                        position. The colours used to be set by `li:nth-child(n)`, so
                        adding this third key would have handed Upcoming the
                        precipitation blue and Precipitation nothing at all. */}
                    <li className="calendar-widget__legend-key--completed"><FontAwesomeIcon icon={ faCalendarCheck }/> <p>Completed Inspection</p></li>
                    {/* SW2-2343 — the third cell marker needs a key like the other two.
                        Next to Completed rather than after Precipitation: both describe
                        inspections, and the pair reads as a before/after. */}
                    <li className="calendar-widget__legend-key--upcoming"><FontAwesomeIcon icon={ faClipboardList }/> <p>Upcoming Inspection</p></li>
                    {/* SW2-2343 — the same corner as Upcoming, in red once the day has
                        passed. A key of its own because the colour change is the only
                        thing distinguishing them, and an unexplained red is alarming
                        rather than informative. */}
                    <li className="calendar-widget__legend-key--past-due"><FontAwesomeIcon icon={ faClipboardList }/> <p>Past Due Inspection</p></li>
                    {/* SW2-2338 — no longer conditional: precipitation covers the
                        whole selection, so the key always describes something
                        reachable. */}
                    <li className="calendar-widget__legend-key--precipitation"><FontAwesomeIcon icon={ faCloudShowersHeavy }/> <p>Daily Precipitation</p></li>
                  </section>
                </ul>
                <span onClick={props.changeShowMore} style={widgetWidth < 450 ? showMoreResponsiveButton : null}>Show {(props.mdSize === 6) ? 'More' : 'Less'} <FontAwesomeIcon icon={faExternalLinkAlt} /></span>
              </footer>
          </div>
        </div>
      </div>
    </>
  )
}


export default Calendar